import { useRef } from "react";
import { Search } from "lucide-react";
import stGeorgeHero from "@/assets/st-george-utah-real-estate-red-rock-landscape.webp";
import stGeorgeHero960 from "@/assets/st-george-utah-real-estate-red-rock-landscape-960w.webp";
import stGeorgeHero1440 from "@/assets/st-george-utah-real-estate-red-rock-landscape-1440w.webp";
import stGeorgeHeroAvif from "@/assets/st-george-utah-real-estate-red-rock-landscape.avif";
import stGeorgeHeroAvif960 from "@/assets/st-george-utah-real-estate-red-rock-landscape-960w.avif";
import stGeorgeHeroAvif1440 from "@/assets/st-george-utah-real-estate-red-rock-landscape-1440w.avif";

const quickSearchCities = ["St George", "Washington", "Santa Clara", "Ivins", "Hurricane"];

const routes: Record<string, string> = {
  "st george": "/search?boardId=97&sort=priceDesc&cityId=21478&status=active",
  "washington": "/search?boardId=97&sort=priceDesc&cityId=21478,14406&status=active",
  "hurricane": "/search?boardId=97&sort=priceDesc&cityId=14371&status=active",
  "ivins": "/search?boardId=97&sort=priceDesc&cityId=14374&status=active",
  "santa clara": "/search?boardId=97&sort=priceDesc&cityId=14396&status=active",
  "toquerville": "/search?boardId=97&sort=priceDesc&cityId=14402&status=active",
  "la verkin": "/search?boardId=97&sort=priceDesc&cityId=14379&status=active",
  "rockville": "/search?boardId=97&sort=priceDesc&cityId=14394&status=active",
  "damron valley": "/search?boardId=97&sort=priceDesc&cityId=14359&status=active",
  "diamond valley": "/search?boardId=97&sort=priceDesc&cityId=21462&status=active",
  "enterprise": "/search?boardId=97&sort=priceDesc&cityId=14363&status=active",
  "central": "/search?boardId=97&sort=priceDesc&cityId=14357&status=active",
  "pine valley": "/search?boardId=97&sort=priceDesc&cityId=14393&status=active",
  "leeds": "/search?boardId=97&sort=priceDesc&cityId=14378&status=active",
  "springdale": "/search?boardId=97&sort=priceDesc&cityId=14399&status=active",
  "veyo": "/search?boardId=97&sort=priceDesc&cityId=14405&status=active",
  "new harmony": "/search?boardId=97&sort=priceDesc&cityId=14388&status=active",
  "gunlock": "/search?boardId=97&sort=priceDesc&cityId=14369&status=active",
  "virgin": "/search?boardId=97&sort=priceDesc&cityId=14407&status=active",
};

const keywordMap = [
  { keywords: ["st george", "saint george", "st george ut", "st george utah"], route: routes["st george"] },
  { keywords: ["washington", "washington ut", "washington utah"], route: routes["washington"] },
  { keywords: ["hurricane", "hurricane ut", "hurricane utah"], route: routes["hurricane"] },
  { keywords: ["ivins", "ivins ut", "ivins utah"], route: routes["ivins"] },
  { keywords: ["santa clara", "santa clara ut", "santa clara utah"], route: routes["santa clara"] },
  { keywords: ["toquerville", "toquerville ut", "toquerville utah"], route: routes["toquerville"] },
  { keywords: ["la verkin", "laverkin", "la verkin ut", "la verkin utah"], route: routes["la verkin"] },
  { keywords: ["rockville", "rockville ut", "rockville utah"], route: routes["rockville"] },
  { keywords: ["damron valley", "dammeron valley", "damron", "dammeron"], route: routes["damron valley"] },
  { keywords: ["diamond valley", "diamond"], route: routes["diamond valley"] },
  { keywords: ["enterprise", "enterprise ut", "enterprise utah"], route: routes["enterprise"] },
  { keywords: ["central", "central ut", "central utah"], route: routes["central"] },
  { keywords: ["pine valley", "pine valley ut", "pine valley utah"], route: routes["pine valley"] },
  { keywords: ["leeds", "leeds ut", "leeds utah"], route: routes["leeds"] },
  { keywords: ["springdale", "springdale ut", "springdale utah"], route: routes["springdale"] },
  { keywords: ["veyo", "veyo ut", "veyo utah"], route: routes["veyo"] },
  { keywords: ["new harmony", "new harmony ut", "new harmony utah"], route: routes["new harmony"] },
  { keywords: ["gunlock", "gunlock ut", "gunlock utah"], route: routes["gunlock"] },
  { keywords: ["virgin", "virgin ut", "virgin utah"], route: routes["virgin"] },
];

const zipMap: Record<string, string> = {
  "84770": routes["st george"],
  "84790": routes["st george"],
  "84791": routes["st george"],
  "84780": routes["washington"],
  "84737": routes["hurricane"],
  "84738": routes["ivins"],
  "84745": routes["la verkin"],
  "84774": routes["toquerville"],
  "84767": routes["rockville"],
  "84763": routes["enterprise"],
  "84722": routes["central"],
  "84783": routes["pine valley"],
  "84765": routes["santa clara"],
};

// All canonical city names for fuzzy matching
const allCityNames = Object.keys(routes);

// Levenshtein distance
function editDistance(a: string, b: string): number {
  const matrix: number[][] = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b[i - 1] === a[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
}

function similarity(a: string, b: string): number {
  const longer = a.length > b.length ? a : b;
  const shorter = a.length > b.length ? b : a;
  const longerLength = longer.length;
  if (longerLength === 0) return 1;
  return (longerLength - editDistance(longer, shorter)) / longerLength;
}

function fuzzyMatch(input: string): string | null {
  let bestScore = 0;
  let bestRoute: string | null = null;

  // Check against all canonical names and all keyword aliases
  for (const item of keywordMap) {
    for (const keyword of item.keywords) {
      const score = similarity(input, keyword);
      if (score > bestScore) {
        bestScore = score;
        bestRoute = item.route;
      }
    }
  }

  return bestScore > 0.75 ? bestRoute : null;
}

function normalizeSearch(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[.,]/g, "")
    .replace(/\s+/g, " ");
}

function runSearch() {
  const inputEl = document.getElementById("home-search") as HTMLInputElement | null;
  if (!inputEl) return;
  const raw = inputEl.value || "";
  const input = normalizeSearch(raw);

  if (!input) {
    window.location.href = "/search";
    return;
  }

  // Priority 1: ZIP exact match
  if (zipMap[input]) {
    window.location.href = zipMap[input];
    return;
  }

  // Priority 2: Exact keyword match
  for (const item of keywordMap) {
    if (item.keywords.includes(input)) {
      window.location.href = item.route;
      return;
    }
  }

  // Priority 3: Partial keyword match (input contains city name)
  for (const item of keywordMap) {
    for (const keyword of item.keywords) {
      if (input.includes(keyword)) {
        window.location.href = item.route;
        return;
      }
    }
  }

  // Priority 4: Fuzzy similarity match
  const fuzzyResult = fuzzyMatch(input);
  if (fuzzyResult) {
    window.location.href = fuzzyResult;
    return;
  }

  // Priority 5: Fallback
  window.location.href = "/search";
}

const Hero = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleQuickSearch = (city: string) => {
    const el = document.getElementById("home-search") as HTMLInputElement | null;
    if (el) {
      el.value = city;
    }
    runSearch();
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-24">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <picture>
          <source
            type="image/avif"
            srcSet={`${stGeorgeHeroAvif960} 960w, ${stGeorgeHeroAvif1440} 1440w, ${stGeorgeHeroAvif} 1920w`}
            sizes="100vw"
          />
          <img
            src={stGeorgeHero}
            srcSet={`${stGeorgeHero960} 960w, ${stGeorgeHero1440} 1440w, ${stGeorgeHero} 1920w`}
            sizes="100vw"
            alt="St. George Utah homes for sale with red rock landscape views in Washington County"
            className="w-full h-full object-cover"
            {...{ fetchpriority: "high" }}
            loading="eager"
            decoding="async"
            width={1920}
            height={1080}
          />
        </picture>

        <div className="hero-overlay absolute inset-0 z-5"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center max-w-4xl mx-auto px-4 md:px-6">
        <p className="text-sm md:text-base text-white/80 uppercase tracking-[0.3em] mb-4">
          Welcome to Southern Utah
        </p>
        <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl heading-luxury text-white mb-4 md:mb-6">
          ST. GEORGE REAL ESTATE — HOMES FOR SALE IN SOUTHERN UTAH
        </h1>
        <p className="text-base sm:text-lg md:text-xl text-white mb-6 md:mb-8 max-w-2xl mx-auto">
          Where Red Rock Dreams Meet Desert Living
        </p>

        {/* Search Bar */}
        <div className="w-full max-w-[900px] mx-auto">
          <div className="flex items-center bg-white rounded-full overflow-hidden shadow-lg">
            <input
              type="text"
              id="home-search"
              ref={inputRef}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  runSearch();
                }
              }}
              placeholder="Search homes by city, address, ZIP, or MLS #"
              className="flex-1 px-5 md:px-6 py-4 md:py-5 text-sm md:text-base text-foreground placeholder:text-muted-foreground bg-transparent outline-none border-none"
            />
            <button
              type="button"
              onClick={() => runSearch()}
              aria-label="Search properties"
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-5 md:px-8 py-4 md:py-5 flex items-center gap-2 transition-colors font-medium text-sm md:text-base shrink-0"
            >
              <Search className="h-5 w-5" />
              <span className="hidden sm:inline">Search</span>
            </button>
          </div>

          {/* Quick Search Buttons */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mt-4">
            {quickSearchCities.map((city) => (
              <button
                key={city}
                type="button"
                onClick={() => handleQuickSearch(city)}
                className="px-4 py-2 text-xs md:text-sm text-white/90 border border-white/30 rounded-full hover:bg-white/20 backdrop-blur-sm transition-colors"
              >
                {city}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="absolute bottom-8 left-0 right-0 z-10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center md:justify-between">
            <div className="hidden lg:flex items-center space-x-8">
              <a href="/buy" className="text-sm text-white hover:text-primary transition-colors duration-300 tracking-wider uppercase">BUY</a>
              <a href="/sell" className="text-sm text-white hover:text-primary transition-colors duration-300 tracking-wider uppercase">SELL</a>
              <a href="/about" className="text-sm text-white hover:text-primary transition-colors duration-300 tracking-wider uppercase">ABOUT</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
